import { db } from '../firebase'
import {
  doc,
  setDoc,
  collection,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  serverTimestamp,
  orderBy,
  writeBatch,
  limit,
  collectionGroup,
} from 'firebase/firestore'

interface StudentData {
  studentId: string
  name: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  instrument?: string
  grade?: number
  lessonDuration?: number
  lessonFrequency?: string
  notes?: string
  profilePicture?: string
  // New fields for enhanced profile
  lessonType?: 'in-person' | 'online' | 'hybrid'
  timeZone?: string
  addressStreet?: string
  addressApt?: string
  addressCity?: string
  addressState?: string
  addressZip?: string
  guardianName?: string
  guardianEmail?: string
  guardianPhone?: string
  guardianPreferredContact?: 'email' | 'phone' | 'text'
  status?: 'active' | 'inactive' | 'trial' | 'pending'
  avatarUrl?: string
  // Remove programs field
}

// Helper function to remove undefined values recursively
const removeUndefinedValues = (obj: any): any => {
  // Handle null and non-object values
  if (obj === null || typeof obj !== 'object') {
    return obj
  }
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues)
  }
  // Handle objects - remove undefined values and recurse into nested objects
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => [key, removeUndefinedValues(value)]),
  )
}

// Helper function to normalize phone numbers for comparison (digits only)
const normalizePhoneForComparison = (phone: string): string => {
  if (!phone) return ''
  return phone.replace(/[^\d]/g, '')
}

// SPAM-CLICK PROTECTION: Lock flag to prevent concurrent student creation
let isAddingStudent = false

// DUPLICATE VALIDATION: Check for existing student by email or phone
const validateUniqueStudent = async (
  teacherId: string,
  email: string,
  phone: string,
  excludeStudentId?: string,
): Promise<{
  isUnique: boolean
  duplicateField?: string
  existingStudent?: any
}> => {
  try {
    const studentsRef = collection(db, 'teachers', teacherId, 'students')
    const studentsSnapshot = await getDocs(studentsRef)

    const normalizedEmail = email.toLowerCase().trim()
    const normalizedPhone = normalizePhoneForComparison(phone)

    for (const doc of studentsSnapshot.docs) {
      // Skip if this is the student being updated
      if (excludeStudentId && doc.id === excludeStudentId) {
        continue
      }

      const studentData = doc.data()

      // Check email match
      if (
        studentData.email &&
        studentData.email.toLowerCase().trim() === normalizedEmail
      ) {
        return {
          isUnique: false,
          duplicateField: 'email',
          existingStudent: { id: doc.id, ...studentData },
        }
      }

      // Check phone match (normalized to digits only)
      if (studentData.phone && normalizedPhone.length >= 8) {
        const existingNormalizedPhone = normalizePhoneForComparison(
          studentData.phone,
        )
        if (existingNormalizedPhone === normalizedPhone) {
          return {
            isUnique: false,
            duplicateField: 'phone',
            existingStudent: { id: doc.id, ...studentData },
          }
        }
      }
    }

    return { isUnique: true }
  } catch (error) {
    console.error('Error validating unique student:', error)
    // Fail open - allow creation if validation fails
    return { isUnique: true }
  }
}

// Add student to teacher's roster
export const addStudentToTeacher = async (
  teacherId: string,
  studentData: StudentData,
) => {
  // SPAM-CLICK PROTECTION: Check if student creation is already in progress
  if (isAddingStudent) {
    throw new Error('Student creation already in progress. Please wait.')
  }

  // SPAM-CLICK PROTECTION: Set lock before starting
  isAddingStudent = true

  try {
    // DUPLICATE VALIDATION: Check for existing student by email or phone
    if (studentData.email || studentData.phone) {
      const validation = await validateUniqueStudent(
        teacherId,
        studentData.email || '',
        studentData.phone || '',
      )

      if (!validation.isUnique) {
        const field = validation.duplicateField
        const existing = validation.existingStudent
        throw new Error(
          `A student with this ${field} already exists: ${existing.name} (${field}: ${existing[field]})`,
        )
      }
    }

    // Create a reference to the teacher's students collection
    const studentRef = collection(db, 'teachers', teacherId, 'students')
    // Normalize email to lowercase for consistent matching
    if (studentData.email) {
      studentData.email = studentData.email.trim().toLowerCase()
    }

    // Check if student already exists
    const existingStudentQuery = query(
      studentRef,
      where('studentId', '==', studentData.studentId),
    )
    const existingStudentSnapshot = await getDocs(existingStudentQuery)
    let studentDocId
    if (!existingStudentSnapshot.empty) {
      // Student already exists, update their information
      const existingStudentDoc = existingStudentSnapshot.docs[0]
      await updateDoc(existingStudentDoc.ref, {
        ...studentData,
        updatedAt: serverTimestamp(),
      })
      studentDocId = existingStudentDoc.id
    } else {
      // Add new student - clean undefined values to prevent Firestore errors
      const cleanedStudentData = removeUndefinedValues({
        ...studentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: studentData.status || 'active', // Default status for new students
      })
      const newStudentRef = await addDoc(studentRef, cleanedStudentData)
      studentDocId = newStudentRef.id
      // Also update the conversation to mark this student as added
      try {
        // Find the conversation between this teacher and student
        const conversationsRef = collection(db, 'conversations')
        const conversationQuery = query(
          conversationsRef,
          where('teacherId', '==', teacherId),
          where('studentId', '==', studentData.studentId),
        )
        const conversationSnapshot = await getDocs(conversationQuery)
        if (!conversationSnapshot.empty) {
          // Update the conversation to mark student as added
          const conversationDoc = conversationSnapshot.docs[0]
          await updateDoc(conversationDoc.ref, {
            studentAdded: true,
            updatedAt: serverTimestamp(),
          })
        }
      } catch (error) {
        console.error('Error updating conversation:', error)
        // Continue with student addition even if conversation update fails
      }
    }
    return studentDocId
  } catch (error) {
    console.error('Error adding student:', error)
    throw error
  } finally {
    // SPAM-CLICK PROTECTION: Always release lock, even on error
    isAddingStudent = false
  }
}

// Check if student is already added to teacher's roster
export const isStudentAddedToTeacher = async (
  teacherId: string,
  studentId: string,
) => {
  try {
    const studentRef = collection(db, 'teachers', teacherId, 'students')
    const studentQuery = query(studentRef, where('studentId', '==', studentId))
    const studentSnapshot = await getDocs(studentQuery)
    return !studentSnapshot.empty
  } catch (error) {
    console.error('Error checking if student is added:', error)
    return false
  }
}

// P1 SCALABILITY FIX: Add query limit constants
const STUDENT_QUERY_LIMIT = 500 // Maximum students to fetch per query

// Get all students for a teacher
// P1 FIX: Added limit() to prevent unbounded queries
export const getTeacherStudents = async (teacherId: string) => {
  try {
    console.log('[P1 FIX] Fetching students with limit for teacher:', teacherId)

    const studentsRef = collection(db, 'teachers', teacherId, 'students')
    const studentsQuery = query(
      studentsRef,
      limit(STUDENT_QUERY_LIMIT), // ✅ CRITICAL: Limit query results
    )
    const studentsSnapshot = await getDocs(studentsQuery)

    console.log(
      `[P1 FIX] Found ${studentsSnapshot.docs.length} students (limit: ${STUDENT_QUERY_LIMIT})`,
    )

    return studentsSnapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        // Format data for UI compatibility
        name:
          data.name ||
          `${data.firstName || ''} ${data.lastName || ''}`.trim() ||
          'Unnamed Student',
        email: data.email || 'No email provided',
        phone: data.phone || 'No phone provided',
        instruments: data.instrument ? [data.instrument] : ['Unspecified'],
        status: data.status || 'active',
        avatar: data.profilePicture || data.avatarUrl || '',
        nextLesson: 'Not scheduled',
        programs: data.programs,
      }
    })
  } catch (error) {
    console.error('Error fetching teacher students:', error)
    throw error
  }
}

// NEW: Generic update function for any student fields with improved error handling
export const updateStudent = async (
  teacherId: string,
  studentDocId: string,
  updates: Partial<StudentData>,
) => {
  try {
    console.log('updateStudent called with:', {
      teacherId,
      studentDocId,
      updates,
    })
    // Validate inputs
    if (!teacherId || !studentDocId) {
      const error = new Error('Missing teacher ID or student document ID')
      console.error('updateStudent validation error:', error)
      throw error
    }
    if (!updates || Object.keys(updates).length === 0) {
      const error = new Error('No updates provided')
      console.error('updateStudent validation error:', error)
      throw error
    }
    // Reference to the student document
    const studentRef = doc(db, 'teachers', teacherId, 'students', studentDocId)
    console.log('Student document reference:', {
      path: studentRef.path,
      id: studentRef.id,
    })
    // Check if the document exists first
    const studentDoc = await getDoc(studentRef)
    if (!studentDoc.exists()) {
      const error = new Error(`Student document not found: ${studentDocId}`)
      console.error('updateStudent error:', error)
      throw error
    }
    console.log('Existing student document data:', studentDoc.data())
    // Prepare the update with timestamp
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    }
    console.log('Update data before cleaning:', updateData)
    // Remove undefined values to prevent Firestore errors
    const cleanedUpdateData = removeUndefinedValues(updateData)
    console.log(
      'Cleaned update data (undefined values removed):',
      cleanedUpdateData,
    )
    // Update the document with cleaned data
    await updateDoc(studentRef, cleanedUpdateData)
    console.log('Student updated successfully:', studentDocId)
    // Verify the update
    const updatedDoc = await getDoc(studentRef)
    console.log('Updated document data:', updatedDoc.data())
    return true
  } catch (error) {
    console.error('updateStudent error:', {
      message: error.message,
      code: error.code,
      name: error.name,
      stack: error.stack,
      teacherId,
      studentDocId,
      updates,
    })
    // Re-throw with more context
    throw new Error(`Failed to update student: ${error.message}`)
  }
}

// Update student notes
export const updateStudentNotes = async (
  teacherId: string,
  studentId: string,
  notes: string,
) => {
  try {
    const studentRef = doc(db, 'teachers', teacherId, 'students', studentId)
    await updateDoc(studentRef, {
      notes: notes,
      updatedAt: serverTimestamp(),
    })
    return true
  } catch (error) {
    console.error('Error updating student notes:', error)
    throw error
  }
}

// Update student progress level
export const updateStudentProgress = async (
  teacherId: string,
  studentId: string,
  progressLevel: string,
) => {
  try {
    const studentRef = doc(db, 'teachers', teacherId, 'students', studentId)
    await updateDoc(studentRef, {
      progressLevel: progressLevel,
      updatedAt: serverTimestamp(),
    })
    return true
  } catch (error) {
    console.error('Error updating student progress level:', error)
    throw error
  }
}

// Get lessons for a specific student
export const getStudentLessons = async (
  teacherId: string,
  studentId: string,
) => {
  try {
    const lessonsRef = collection(
      db,
      'teachers',
      teacherId,
      'students',
      studentId,
      'lessons',
    )
    const lessonsQuery = query(lessonsRef, orderBy('date', 'asc'))
    const lessonsSnapshot = await getDocs(lessonsQuery)
    return lessonsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))
  } catch (error) {
    console.error('Error fetching student lessons:', error)
    throw error
  }
}

// Add a new lesson for a student
export const addStudentLesson = async (
  teacherId: string,
  studentId: string,
  lessonData: any,
) => {
  try {
    if (!teacherId || !studentId) {
      throw new Error('Missing teacher ID or student ID')
    }
    // Validate required fields
    if (!lessonData.date || !lessonData.time || !lessonData.instrument) {
      throw new Error(
        'Missing required lesson data (date, time, or instrument)',
      )
    }
    // Create a timestamp for consistent data - using new Date() instead of serverTimestamp()
    // serverTimestamp() can cause issues as it's a special server-side value
    const now = new Date()
    // Prepare the lesson data with all required fields
    const lessonToSave = {
      ...lessonData,
      teacherId,
      studentId,
      createdAt: now,
      updatedAt: now,
      status: lessonData.status || 'scheduled',
    }
    // Reference to the student's lessons collection
    const lessonsRef = collection(
      db,
      'teachers',
      teacherId,
      'students',
      studentId,
      'lessons',
    )
    // Add to student's lessons collection
    const newLessonRef = await addDoc(lessonsRef, lessonToSave)
    if (!newLessonRef || !newLessonRef.id) {
      throw new Error('Failed to create lesson document')
    }
    // Get the new lesson ID
    const newLessonId = newLessonRef.id
    // Prepare calendar entry data
    const calendarEntry = {
      lessonId: newLessonId,
      studentId: studentId,
      studentName: lessonData.studentName || 'Student',
      title: lessonData.title || `${lessonData.instrument} Lesson`,
      date: lessonData.date,
      time: lessonData.time,
      duration: lessonData.duration,
      instrument: lessonData.instrument,
      location: lessonData.location,
      notes: lessonData.notes,
      status: lessonData.status || 'scheduled',
      createdAt: now,
      updatedAt: now,
    }
    // Add to teacher's main calendar
    const teacherCalendarRef = collection(db, 'teachers', teacherId, 'calendar')
    await addDoc(teacherCalendarRef, calendarEntry)
    console.log('Lesson successfully added with ID:', newLessonId)
    return newLessonId
  } catch (error) {
    console.error('Error adding student lesson:', error)
    throw error
  }
}

// Update an existing lesson
export const updateStudentLesson = async (
  teacherId: string,
  studentId: string,
  lessonData: any,
) => {
  try {
    if (!teacherId || !studentId || !lessonData.id) {
      throw new Error('Missing teacher ID, student ID, or lesson ID')
    }
    // Reference to the lesson document
    const lessonRef = doc(
      db,
      'teachers',
      teacherId,
      'students',
      studentId,
      'lessons',
      lessonData.id,
    )
    // Create a timestamp for consistent data
    const now = serverTimestamp()
    // Prepare the lesson update data
    const lessonUpdate = {
      ...lessonData,
      updatedAt: now,
    }
    // Update in student's lessons collection
    await updateDoc(lessonRef, lessonUpdate)
    // Reference to teacher's calendar collection
    const teacherCalendarRef = collection(db, 'teachers', teacherId, 'calendar')
    // Query to find the calendar entry for this lesson
    const calendarQuery = query(
      teacherCalendarRef,
      where('lessonId', '==', lessonData.id),
      where('studentId', '==', studentId),
    )
    // Get matching calendar entries
    const calendarSnapshot = await getDocs(calendarQuery)
    if (!calendarSnapshot.empty) {
      // Update the calendar entry
      const calendarDoc = calendarSnapshot.docs[0]
      await updateDoc(calendarDoc.ref, {
        date: lessonData.date,
        time: lessonData.time,
        duration: lessonData.duration,
        instrument: lessonData.instrument,
        location: lessonData.location,
        notes: lessonData.notes,
        status: lessonData.status || 'scheduled',
        updatedAt: now,
      })
    } else {
      // If no calendar entry exists, create one
      await addDoc(teacherCalendarRef, {
        lessonId: lessonData.id,
        studentId: studentId,
        studentName: lessonData.studentName || 'Student',
        date: lessonData.date,
        time: lessonData.time,
        duration: lessonData.duration,
        instrument: lessonData.instrument,
        location: lessonData.location,
        notes: lessonData.notes,
        status: lessonData.status || 'scheduled',
        createdAt: now,
        updatedAt: now,
      })
    }
    console.log('Lesson successfully updated:', lessonData.id)
    return true
  } catch (error) {
    console.error('Error updating student lesson:', error)
    throw error
  }
}

// Delete a lesson
export const deleteStudentLesson = async (
  teacherId: string,
  studentId: string,
  lessonId: string,
) => {
  try {
    // Delete from student's lessons collection
    const lessonRef = doc(
      db,
      'teachers',
      teacherId,
      'students',
      studentId,
      'lessons',
      lessonId,
    )
    await deleteDoc(lessonRef)
    // Also delete from teacher's main calendar
    const teacherCalendarRef = collection(db, 'teachers', teacherId, 'calendar')
    const calendarQuery = query(
      teacherCalendarRef,
      where('lessonId', '==', lessonId),
      where('studentId', '==', studentId),
    )
    const calendarSnapshot = await getDocs(calendarQuery)
    if (!calendarSnapshot.empty) {
      const calendarDoc = calendarSnapshot.docs[0]
      await deleteDoc(calendarDoc.ref)
    }
    return true
  } catch (error) {
    console.error('Error deleting student lesson:', error)
    throw error
  }
}

// Delete student account and all associated data
export const deleteStudentAccount = async (
  studentId: string,
  teacherId: string,
): Promise<void> => {
  try {
    // Delete the student document from Firestore
    const studentRef = doc(db, 'teachers', teacherId, 'students', studentId)
    await deleteDoc(studentRef)
    console.log(`Successfully deleted student ${studentId} from Firestore`)
  } catch (error) {
    console.error('Error deleting student from Firestore:', error)
    throw new Error('Failed to delete student from database')
  }
}

/**
 * Match a newly signed-up student to teacher(s) who already added them by email.
 *
 * How it works:
 * 1. Queries ALL teachers' student subcollections (collectionGroup) for matching email
 * 2. For each match, updates the teacher's student record with `linkedUid` (the student's Firebase UID)
 * 3. Extracts the teacherId from each matched document's path
 * 4. Returns the array of matched teacherIds
 *
 * Requirements:
 * - A collectionGroup index on `students` for the `email` field (see firestore.indexes.json)
 * - Firestore rules must allow the collectionGroup query for authenticated users
 *
 * @param studentEmail - The email the student used to sign up
 * @param studentUid - The student's Firebase Auth UID
 * @returns Array of teacherIds that had this student in their roster
 */
export const matchStudentToTeacher = async (
  studentEmail: string,
  studentUid: string,
): Promise<string[]> => {
  try {
    if (!studentEmail || !studentUid) {
      console.warn(
        '[matchStudentToTeacher] Missing email or UID, skipping match',
      )
      return []
    }

    const normalizedEmail = studentEmail.toLowerCase().trim()
    console.log(
      `[matchStudentToTeacher] Searching for email: ${normalizedEmail}`,
    )

    // Query all teachers' student subcollections for matching email
    const studentsGroupRef = collectionGroup(db, 'students')
    const matchQuery = query(
      studentsGroupRef,
      where('email', '==', normalizedEmail),
    )
    const matchSnapshot = await getDocs(matchQuery)

    if (matchSnapshot.empty) {
      console.log('[matchStudentToTeacher] No matching teacher records found')
      return []
    }

    console.log(
      `[matchStudentToTeacher] Found ${matchSnapshot.docs.length} match(es)`,
    )

    const matchedTeacherIds: string[] = []

    for (const matchDoc of matchSnapshot.docs) {
      try {
        // Extract teacherId from the document path: teachers/{teacherId}/students/{docId}
        // matchDoc.ref.path = "teachers/abc123/students/xyz789"
        const pathSegments = matchDoc.ref.path.split('/')
        const teacherId = pathSegments[1] // "teachers" is [0], teacherId is [1]

        if (!teacherId) {
          console.warn(
            '[matchStudentToTeacher] Could not extract teacherId from path:',
            matchDoc.ref.path,
          )
          continue
        }

        // Skip if this record is already linked to a different UID
        const existingData = matchDoc.data()
        if (existingData.linkedUid && existingData.linkedUid !== studentUid) {
          console.warn(
            `[matchStudentToTeacher] Record already linked to different UID: ${existingData.linkedUid}, skipping`,
          )
          continue
        }

        // Skip if already linked to this same UID (idempotent)
        if (existingData.linkedUid === studentUid) {
          console.log(
            `[matchStudentToTeacher] Already linked to teacher ${teacherId}, skipping update`,
          )
          matchedTeacherIds.push(teacherId)
          continue
        }

        // Update the teacher's student record with the real Firebase UID
        await updateDoc(matchDoc.ref, {
          linkedUid: studentUid,
          linkedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })

        console.log(
          `[matchStudentToTeacher] Linked student ${studentUid} to teacher ${teacherId} (doc: ${matchDoc.id})`,
        )
        matchedTeacherIds.push(teacherId)
      } catch (docError) {
        console.error(
          `[matchStudentToTeacher] Error processing match doc ${matchDoc.id}:`,
          docError,
        )
        // Continue processing other matches even if one fails
      }
    }

    console.log(
      `[matchStudentToTeacher] Successfully matched ${matchedTeacherIds.length} teacher(s):`,
      matchedTeacherIds,
    )
    return matchedTeacherIds
  } catch (error) {
    console.error('[matchStudentToTeacher] Error during matching:', error)
    // Fail gracefully — don't block student signup if matching fails
    return []
  }
}

/**
 * Self-link: called from the student side (e.g. StudentDashboard) to ensure
 * that any teacher roster records matching the student's email are linked
 * to the student's Firebase UID. This covers the case where the teacher
 * added the student AFTER the student had already signed up.
 *
 * Safe to call repeatedly — idempotent (skips already-linked records).
 */
export const selfLinkStudentToTeachers = async (
  studentEmail: string,
  studentUid: string,
): Promise<number> => {
  try {
    if (!studentEmail || !studentUid) {
      console.warn(
        '[selfLinkStudent] ⚠️ Missing email or UID, skipping. email:',
        studentEmail,
        'uid:',
        studentUid,
      )
      return 0
    }

    const normalizedEmail = studentEmail.toLowerCase().trim()
    console.log(
      `🔍 [selfLinkStudent] Querying collectionGroup("students") where email == "${normalizedEmail}"`,
    )

    // Query all teachers' student subcollections for matching email
    const studentsGroupRef = collectionGroup(db, 'students')
    const matchQuery = query(
      studentsGroupRef,
      where('email', '==', normalizedEmail),
    )
    const matchSnapshot = await getDocs(matchQuery)

    console.log(
      `🔍 [selfLinkStudent] Query returned ${matchSnapshot.size} doc(s)`,
    )

    // If server-side query found no matches, try client-side case-insensitive fallback
    // This handles legacy data where emails were stored with mixed case
    let docsToProcess = matchSnapshot.docs
    if (matchSnapshot.empty) {
      console.log(
        '🔍 [selfLinkStudent] Server-side email query returned 0 docs. Trying client-side case-insensitive fallback...',
      )
      try {
        const allStudentsSnap = await getDocs(collectionGroup(db, 'students'))
        const caseInsensitiveMatches = allStudentsSnap.docs.filter((d) => {
          const docEmail = d.data().email
          if (!docEmail || typeof docEmail !== 'string') return false
          return docEmail.trim().toLowerCase() === normalizedEmail
        })
        console.log(
          `🔍 [selfLinkStudent] Client-side fallback found ${caseInsensitiveMatches.length} case-insensitive match(es)`,
        )
        if (caseInsensitiveMatches.length > 0) {
          docsToProcess = caseInsensitiveMatches
        }
      } catch (fallbackError: any) {
        console.warn(
          '⚠️ [selfLinkStudent] Client-side fallback query failed:',
          fallbackError?.code || fallbackError?.message,
        )
      }
    }

    if (docsToProcess.length === 0) {
      console.warn(
        '⚠️ [selfLinkStudent] No matching teacher records found for email:',
        normalizedEmail,
      )
      console.warn('   Possible causes:')
      console.warn('   1. Teacher has not added this student yet')
      console.warn(
        '   2. collectionGroup index for students/email not deployed',
      )
      console.warn('   3. Firestore rules blocking collectionGroup read')
      return 0
    }

    let linkedCount = 0

    for (const matchDoc of docsToProcess) {
      try {
        const existingData = matchDoc.data()
        console.log(`🔍 [selfLinkStudent] Found doc: ${matchDoc.ref.path}`, {
          email: existingData.email,
          linkedUid: existingData.linkedUid || '(NOT SET)',
          name: existingData.name,
        })

        // Skip if already linked to this UID (idempotent)
        if (existingData.linkedUid === studentUid) {
          console.log(
            `✅ [selfLinkStudent] Already linked to this UID, skipping`,
          )
          continue
        }

        // Skip if linked to a DIFFERENT UID (belongs to someone else)
        if (existingData.linkedUid && existingData.linkedUid !== studentUid) {
          console.warn(
            `⚠️ [selfLinkStudent] Record ${matchDoc.id} already linked to different UID: ${existingData.linkedUid}, skipping`,
          )
          continue
        }

        // linkedUid is missing — set it now
        console.log(
          `🔧 [selfLinkStudent] Setting linkedUid on ${matchDoc.ref.path} to ${studentUid}`,
        )
        await updateDoc(matchDoc.ref, {
          linkedUid: studentUid,
          linkedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })

        const pathSegments = matchDoc.ref.path.split('/')
        const teacherId = pathSegments[1]
        console.log(
          `✅ [selfLinkStudent] Successfully linked to teacher ${teacherId} (doc: ${matchDoc.id})`,
        )
        linkedCount++
      } catch (docError: any) {
        console.error(
          `❌ [selfLinkStudent] Error processing doc ${matchDoc.id}:`,
          docError?.code || docError?.message || docError,
        )
        console.error('   Full error:', docError)
      }
    }

    if (linkedCount > 0) {
      console.log(
        `✅ [selfLinkStudent] Successfully linked ${linkedCount} teacher record(s)`,
      )
    } else {
      console.log(
        '🔍 [selfLinkStudent] No new links needed (all already linked or skipped)',
      )
    }

    return linkedCount
  } catch (error: any) {
    console.error(
      '❌ [selfLinkStudent] CRITICAL ERROR during self-linking:',
      error?.code || error?.message || error,
    )
    console.error('   Full error:', error)
    return 0
  }
}
